import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API_BASE,
    headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('business');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth
export const authApi = {
    login: (data) => api.post('/auth/login', data),
    getMe: () => api.get('/auth/me'),
};

// Registration
export const registerApi = {
    register: (data) => api.post('/register', data),
    validateCoupon: (code) => api.get('/register/validate-coupon', { params: { code } }),
};

// Business
export const businessApi = {
    getCurrent: () => api.get('/business'),
    updateBranding: (data) => api.put('/business/branding', data),
    getFeatures: () => api.get('/business/features'),
    updateFeatures: (features) => api.put('/business/features', { enabled_features: features }),
};

// Users
export const usersApi = {
    getAll: () => api.get('/users'),
    create: (data) => api.post('/users', data),
    update: (id, data) => api.put(`/users/${id}`, data),
    delete: (id) => api.delete(`/users/${id}`),
    updateRole: (userId, role) => api.put(`/users/${userId}/role`, { role }),
    toggleStatus: (userId, isActive) => api.put(`/users/${userId}/status`, null, { params: { is_active: isActive } }),
    changePassword: (data) => api.put('/users/change-password', data),
    sendPasswordReset: (userId) => api.post(`/users/${userId}/send-password-reset`),
};

// Password Reset (public)
export const resetApi = {
    validateToken: (token) => api.get(`/auth/reset-password/${token}`),
    resetPassword: (token, newPassword) => api.post(`/auth/reset-password/${token}`, { new_password: newPassword }),
};

// Admin
export const adminApi = {
    getBusinesses: () => api.get('/admin/businesses'),
    switchContext: (businessId) => api.post('/admin/switch-context', { business_id: businessId }),
    clearContext: () => api.post('/admin/clear-context'),
    updateSubscription: (businessId, price) => api.put(`/admin/businesses/${businessId}/subscription`, { monthly_price: price }),
    updateBusinessStatus: (businessId, isActive, subscriptionStatus) => api.put(`/admin/businesses/${businessId}/status`, null, { params: { is_active: isActive, subscription_status: subscriptionStatus } }),
    deleteBusiness: (businessId) => api.delete(`/admin/businesses/${businessId}`),
    getPlatformSettings: () => api.get('/admin/platform-settings'),
    getCoupons: () => api.get('/admin/coupons'),
    createCoupon: (data) => api.post('/admin/coupons', data),
    updateCoupon: (id, data) => api.put(`/admin/coupons/${id}`, data),
    deleteCoupon: (id) => api.delete(`/admin/coupons/${id}`),
    // Admin user management
    getAdmins: () => api.get('/admin/users'),
    createAdmin: (data) => api.post('/admin/users', data),
    updateAdmin: (id, data) => api.put(`/admin/users/${id}`, data),
    deleteAdmin: (id) => api.delete(`/admin/users/${id}`),
};

// Payments
export const paymentsApi = {
    getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Safe Methods
export const safeMethodsApi = {
    getAll: (category) => api.get('/safe-methods', { params: { category } }),
    getById: (id) => api.get(`/safe-methods/${id}`),
};

// Daily Diary
export const diaryApi = {
    getAll: (params) => api.get('/diary', { params }),
    create: (data) => api.post('/diary', data),
    updateItem: (entryId, data) => api.put(`/diary/${entryId}/item`, data),
    signOff: (entryId) => api.put(`/diary/${entryId}/sign-off`),
};

// Temperature Logs
export const temperatureApi = {
    getAll: (params) => api.get('/temperature-logs', { params }),
    create: (data) => api.post('/temperature-logs', data),
    getHistory: (params) => api.get('/temperature-logs/history', { params }),
};

// Saved Items (Temperature Locations)
export const savedItemsApi = {
    getAll: () => api.get('/saved-items'),
    create: (data) => api.post('/saved-items', data),
    update: (id, data) => api.put(`/saved-items/${id}`, data),
    delete: (id) => api.delete(`/saved-items/${id}`),
    getBarcode: (id) => api.get(`/saved-items/${id}/barcode`, { responseType: 'blob' }),
    lookupByBarcode: (barcode) => api.get(`/saved-items/lookup/${encodeURIComponent(barcode)}`),
};

// Suppliers
export const suppliersApi = {
    getAll: () => api.get('/suppliers'),
    create: (data) => api.post('/suppliers', data),
    update: (id, data) => api.put(`/suppliers/${id}`, data),
    delete: (id) => api.delete(`/suppliers/${id}`),
};

// Training
export const trainingApi = {
    getAll: (userId) => api.get('/training', { params: { user_id: userId } }),
    create: (data) => api.post('/training', data),
};

// Incidents
export const incidentsApi = {
    getAll: (params) => api.get('/incidents', { params }),
    create: (data) => api.post('/incidents', data),
    resolve: (id, correctiveAction) => api.put(`/incidents/${id}/resolve`, null, { params: { corrective_action: correctiveAction } }),
};

// Reviews
export const reviewsApi = {
    getAll: () => api.get('/reviews'),
    create: (data) => api.post('/reviews', data),
};

// Dashboard
export const dashboardApi = {
    getStats: () => api.get('/dashboard/stats'),
};

// Reports
export const reportsApi = {
    getCompliance: (startDate, endDate) => api.get('/reports/compliance', { params: { start_date: startDate, end_date: endDate } }),
    getCompliancePdf: (startDate, endDate) => api.get('/reports/compliance/pdf', { params: { start_date: startDate, end_date: endDate }, responseType: 'blob' }),
};

// Allergens & Menu
export const allergensApi = {
    getAll: () => api.get('/allergens'),
    getMenuItems: (category) => api.get('/menu-items', { params: { category } }),
    createMenuItem: (data) => api.post('/menu-items', data),
    updateMenuItem: (id, data) => api.put(`/menu-items/${id}`, data),
    deleteMenuItem: (id) => api.delete(`/menu-items/${id}`),
    getMatrix: () => api.get('/allergen-matrix'),
};

// Notifications
export const notificationsApi = {
    getTrainingStatus: () => api.get('/notifications/training-status'),
    sendTrainingExpiry: () => api.post('/notifications/training-expiry'),
};

// Subscription
export const subscriptionApi = {
    getDetails: () => api.get('/business/subscription'),
    cancel: () => api.post('/business/cancel-subscription'),
    renew: () => api.post('/business/renew-subscription'),
};

// Calibration Logs
export const calibrationApi = {
    getAll: (params) => api.get('/calibration-logs', { params }),
    create: (data) => api.post('/calibration-logs', data),
    signOff: (id) => api.put(`/calibration-logs/${id}/sign-off`),
};

// Delivery Records
export const deliveryApi = {
    getAll: (params) => api.get('/delivery-records', { params }),
    create: (data) => api.post('/delivery-records', data),
};

// Storage Labels
export const labelsApi = {
    getTemplates: (params) => api.get('/label-templates', { params }),
    createTemplate: (data) => api.post('/label-templates', data),
    updateTemplate: (id, data) => api.put(`/label-templates/${id}`, data),
    deleteTemplate: (id) => api.delete(`/label-templates/${id}`),
    getPrintData: (id, customHours) => api.post(`/label-templates/${id}/print-data`, null, { params: customHours ? { custom_use_by_hours: customHours } : {} }),
};

// Contact
export const contactApi = {
    submit: (data) => api.post('/contact', data),
};

export default api;
